/**
 * CreateTreeModal Component
 *
 * Modal for creating a new family tree.
 * Shows two options:
 * 1. Create from my profile (PRIMARY - FUNCTIONAL)
 * 2. Import GEDCOM (NOW ENABLED - Phase 4 Slice 1)
 *
 * Used in NO-TREE dashboard state.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as Dialog from '@radix-ui/react-dialog';
import { GedcomUploadModal } from '@/features/gedcom/components/GedcomUploadModal';
import type { UploadedFileMetadata } from '@/features/gedcom/hooks/useGedcomUpload';

interface CreateTreeModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateFromProfile: () => Promise<void>;
}

export function CreateTreeModal({ isOpen, onClose, onCreateFromProfile }: CreateTreeModalProps) {
  const { t } = useTranslation('common');
  const [isCreating, setIsCreating] = useState(false);
  const [isGedcomModalOpen, setIsGedcomModalOpen] = useState(false);

  const handleCreateFromProfile = async () => {
    setIsCreating(true);
    try {
      await onCreateFromProfile();
      onClose();
    } catch (error) {
      console.error('[CreateTreeModal] Failed to create tree:', error);
      // Error handling is done in parent component (toast)
    } finally {
      setIsCreating(false);
    }
  };

  const handleGedcomClick = () => {
    // Open GEDCOM upload modal
    setIsGedcomModalOpen(true);
  };

  const handleGedcomUploadSuccess = (metadata: UploadedFileMetadata) => {
    console.log('[CreateTreeModal] GEDCOM upload success:', metadata);
    // Close GEDCOM modal
    setIsGedcomModalOpen(false);
    // Close create tree modal
    onClose();
    // TODO Slice 2: Parse GEDCOM and create tree
    // For now, just show success message
    alert(`File uploaded successfully!\n\nFile: ${metadata.fileName}\nEncoding: ${metadata.detectedEncoding.encoding}\n\nParsing will be implemented in Slice 2.`);
  };

  const handleGedcomModalClose = () => {
    setIsGedcomModalOpen(false);
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={(open) => !open && !isCreating && onClose()}>
      <Dialog.Portal>
        {/* Backdrop */}
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50 animate-in fade-in" />

        {/* Modal Content */}
        <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-[95vw] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[700px] bg-white dark:bg-slate-800 rounded-lg sm:rounded-xl shadow-xl animate-in fade-in zoom-in-95 max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-4 sm:p-5 md:p-6 border-b border-slate-200 dark:border-slate-700">
            <Dialog.Title className="text-lg sm:text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
              {t('tree.createModal.title', 'Create Your Family Tree')}
            </Dialog.Title>
            <Dialog.Description className="text-xs sm:text-sm md:text-base text-slate-500 dark:text-slate-400 mt-1 sm:mt-1.5 md:mt-2">
              {t('tree.createModal.description', 'Choose how you want to start building your family tree')}
            </Dialog.Description>
          </div>

          {/* Options */}
          <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
            {/* Option A: Create from my profile (PRIMARY - FUNCTIONAL) */}
            <button
              onClick={handleCreateFromProfile}
              disabled={isCreating}
              className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-primary bg-primary/5 hover:bg-primary/10 rounded-lg sm:rounded-xl text-left transition-colors group disabled:opacity-50 disabled:cursor-not-allowed min-h-[88px] sm:min-h-[96px]"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="size-10 sm:size-11 md:size-12 lg:size-14 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                  <span className="material-symbols-outlined text-xl sm:text-2xl md:text-[26px] lg:text-3xl text-primary">
                    person
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-slate-900 dark:text-white mb-0.5 sm:mb-1">
                    {t('tree.createModal.fromProfile.title', 'Create from my profile')}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-400">
                    {t(
                      'tree.createModal.fromProfile.description',
                      'Start a new tree with yourself as the first person. You can add family members later.'
                    )}
                  </p>
                  {isCreating && (
                    <div className="flex items-center gap-2 mt-1.5 sm:mt-2 text-xs sm:text-sm text-primary">
                      <div className="size-3 sm:size-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                      <span>{t('tree.createModal.creating', 'Creating tree...')}</span>
                    </div>
                  )}
                </div>
                {!isCreating && (
                  <span className="material-symbols-outlined text-lg sm:text-xl md:text-2xl text-primary group-hover:translate-x-1 transition-transform flex-shrink-0">
                    arrow_forward
                  </span>
                )}
              </div>
            </button>

            {/* Option B: Import GEDCOM (NOW ENABLED) */}
            <button
              onClick={handleGedcomClick}
              disabled={isCreating}
              className="w-full p-3 sm:p-4 md:p-5 lg:p-6 border-2 border-slate-200 dark:border-slate-700 hover:border-primary hover:bg-primary/5 rounded-lg sm:rounded-xl text-left transition-colors group disabled:opacity-50 disabled:cursor-not-allowed min-h-[88px] sm:min-h-[96px]"
            >
              <div className="flex items-start gap-3 sm:gap-4">
                <div className="size-10 sm:size-11 md:size-12 lg:size-14 rounded-full bg-slate-200 dark:bg-slate-700 group-hover:bg-primary/10 flex items-center justify-center flex-shrink-0 transition-colors">
                  <span className="material-symbols-outlined text-xl sm:text-2xl md:text-[26px] lg:text-3xl text-slate-500 dark:text-slate-400 group-hover:text-primary transition-colors">
                    upload_file
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm sm:text-base md:text-lg font-semibold text-slate-900 dark:text-white mb-0.5 sm:mb-1">
                    {t('tree.createModal.importGedcom.title', 'Import GEDCOM file')}
                  </h3>
                  <p className="text-xs sm:text-sm md:text-base text-slate-600 dark:text-slate-400">
                    {t(
                      'tree.createModal.importGedcom.description',
                      'Import an existing family tree from a GEDCOM file.'
                    )}
                  </p>
                </div>
                <span className="material-symbols-outlined text-lg sm:text-xl md:text-2xl text-slate-400 group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0">
                  arrow_forward
                </span>
              </div>
            </button>
          </div>

          {/* Footer */}
          <div className="p-4 sm:p-5 md:p-6 border-t border-slate-200 dark:border-slate-700 flex justify-end">
            <button
              onClick={onClose}
              disabled={isCreating}
              className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-xs sm:text-sm md:text-base font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md sm:rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {t('actions.cancel', 'Cancel')}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>

      {/* GEDCOM Upload Modal */}
      <GedcomUploadModal
        isOpen={isGedcomModalOpen}
        onClose={handleGedcomModalClose}
        onSuccess={handleGedcomUploadSuccess}
      />
    </Dialog.Root>
  );
}
